import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "@chakra-ui/react";
import PermissionDefinitionsEditor from "./PermissionDefinitionsEditor";

const PermissionDefinitionsEditorPage = () => {
  const { permissionDefinitionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [permissionDefinition, setPermissionDefinition] = useState(null);
  const [loading, setLoading] = useState(true);

  const isNew = permissionDefinitionId === "new";

  const createEmptyPermissionDefinition = () => ({
    _id: crypto.randomUUID(),
    code: "",
    description: "",
    createdDate: new Date().toISOString(),
  });

  useEffect(() => {
    const fetchPermissionDefinition = async () => {
      // Check if we have the data in navigation state
      if (location.state?.permissionDefinition) {
        setPermissionDefinition(location.state.permissionDefinition);
        setLoading(false);
      } else if (isNew) {
        setPermissionDefinition(createEmptyPermissionDefinition());
        setLoading(false);
      } else {
        // Fallback to fetching (but this may fail due to CORS on individual endpoints)
        try {
          const res = await fetch(`https://permissions.poc.mrc.ai/api/definitions/${permissionDefinitionId}`);
          const data = await res.json();
          setPermissionDefinition(data);
        } catch (error) {
          console.error("Error fetching permission definition:", error);
          // If fetch fails, redirect back to the permissions tab
          navigate("/");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchPermissionDefinition();
  }, [permissionDefinitionId, isNew, location.state, navigate]);

  const handleSave = async (updatedPermissionDefinition) => {
    try {
      const method = isNew ? "POST" : "PUT";
      const url = isNew
        ? `https://permissions.poc.mrc.ai/api/definitions`
        : `https://permissions.poc.mrc.ai/api/definitions/${permissionDefinitionId}`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedPermissionDefinition),
      });

      if (!res.ok) throw new Error("Failed to save permission definition");

      const saved = await res.json();
      setPermissionDefinition(saved);
    } catch (err) {
      console.error("Error saving permission definition:", err);
    }
  };

  const handleClose = () => navigate("/");

  if (loading) return <Spinner />;

  return <PermissionDefinitionsEditor permissionDefinition={permissionDefinition} onClose={handleClose} isNew={isNew} />;
};

export default PermissionDefinitionsEditorPage;
